import { useMemo, useState } from "react";
import type { WorkItem, WorkKind } from "@shared/types.js";
import { fnum } from "../lib/format.js";

const KIND_LABEL: Record<WorkKind, string> = {
  reprice: "Re-price",
  price: "Price",
  crosscheck: "Crosscheck",
  export: "Export",
  estimate: "Estimate",
};

const KIND_PILL: Record<WorkKind, string> = {
  reprice: "p-high",
  price: "p-await",
  crosscheck: "p-est",
  export: "p-done",
  estimate: "p-none",
};

const SHOW = 150;

export function Worklist({
  items, onExport,
}: {
  items: WorkItem[];
  onExport: (kind: "csv" | "copy") => void;
}) {
  const [kind, setKind] = useState<WorkKind | "">("");
  const [mineOnly, setMineOnly] = useState("");

  const byKind = useMemo(() => {
    const m = new Map<WorkKind, number>();
    for (const i of items) m.set(i.kind, (m.get(i.kind) ?? 0) + 1);
    return m;
  }, [items]);

  const owners = useMemo(() => {
    const s = new Set<string>();
    for (const i of items) if (i.owner) s.add(i.owner);
    return [...s].sort((a, b) => a.localeCompare(b));
  }, [items]);

  const shown = useMemo(
    () => items.filter((i) =>
      (!kind || i.kind === kind) && (!mineOnly || i.owner === mineOnly)),
    [items, kind, mineOnly],
  );

  /* The two bands that are genuinely unblocked right now. */
  const unblocked = items.filter((i) => i.kind === "price" || i.kind === "reprice").length;
  const oldest = items.find((i) => i.ageDays !== null);

  return (
    <>
      <div className="sec">
        <div className="sec-head">
          <span className="eyebrow">Section 2</span>
          <h2><i className="bi bi-list-check hico" aria-hidden="true" />Worklist</h2>
          <span className="note">
            Outstanding work, most urgent first &middot; {fnum(items.length)} item
            {items.length === 1 ? "" : "s"} in scope
          </span>
        </div>

        <div className="callout">
          <span className="ic" />
          <div>
            <h3>
              {fnum(unblocked)} item{unblocked === 1 ? "" : "s"} can be worked on right now
            </h3>
            <p>
              These have the contract rate in hand &mdash; nothing external is blocking them.
              {oldest?.ageDays != null && (
                <> The longest has been waiting <b>{fnum(oldest.ageDays)} days</b>.</>
              )}{" "}
              The remaining items need an estimate because no CR has arrived yet.
            </p>
          </div>
        </div>
      </div>

      <div className="sec">
        <div className="card">
          <div className="card-head">
            <h3><i className="bi bi-card-checklist hico" aria-hidden="true" />{fnum(shown.length)} shown</h3>
            <div style={{ display: "flex", gap: 7, marginLeft: "auto", flexWrap: "wrap", alignItems: "center" }}>
              <div className="seg" role="group" aria-label="Action type">
                <button type="button" aria-pressed={kind === ""} onClick={() => setKind("")}>
                  All
                </button>
                {(["reprice", "price", "crosscheck", "export", "estimate"] as WorkKind[])
                  .filter((k) => byKind.has(k))
                  .map((k) => (
                    <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
                      {KIND_LABEL[k]} {byKind.get(k)}
                    </button>
                  ))}
              </div>
              {owners.length > 0 && (
                <select value={mineOnly} onChange={(e) => setMineOnly(e.target.value)} aria-label="Owner">
                  <option value="">Anyone</option>
                  {owners.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              )}
              <button className="btn btn--sm btn--primary" type="button" onClick={() => onExport("csv")}>
                Download CSV
              </button>
              <button className="btn btn--sm" type="button" onClick={() => onExport("copy")}>
                Copy for Excel
              </button>
            </div>
          </div>

          <div className="tablewrap" style={{ maxHeight: "70vh", overflowY: "auto" }}>
            <table>
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Supplier</th>
                  <th className="n">Code</th>
                  <th>Component</th>
                  <th>Region</th>
                  <th>Cat</th>
                  <th>Last touched by</th>
                  <th className="n">Waiting</th>
                  <th>Where in the Masterfile</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <div className="empty">Nothing outstanding for this filter. </div>
                    </td>
                  </tr>
                )}
                {shown.slice(0, SHOW).map((i, n) => (
                  <tr key={`${i.uid}-${n}`}>
                    <td>
                      <span className={`pill ${KIND_PILL[i.kind]}`}>{KIND_LABEL[i.kind]}</span>
                    </td>
                    <td className="strong">{i.name ?? "(no name)"}</td>
                    <td className="n">{i.code ?? "–"}</td>
                    <td>{i.compLabel}</td>
                    <td>{i.region ?? "–"}</td>
                    <td>
                      {i.cat
                        ? <span className="catbadge">{i.cat}</span>
                        : <span className="catbadge" title="missing">&mdash;</span>}
                    </td>
                    <td>{i.owner ?? <span className="muted">unassigned</span>}</td>
                    <td className="n">
                      {i.ageDays === null
                        ? <span className="muted">&ndash;</span>
                        : <b>{fnum(i.ageDays)}d</b>}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {i.sheet} row {i.excelRow}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {shown.length > SHOW && (
            <div className="tblnote">
              Showing the first {SHOW} of {fnum(shown.length)} &mdash; narrow with the filters
              above, or export the full list to hand out.
            </div>
          )}
          <div className="tblnote">
            <b>Waiting</b> counts days since the CR arrived (to price) or since the entry was made
            (to crosscheck). Estimation items have no waiting clock because no CR has arrived.
            Each record appears once, under its most urgent next step.
          </div>
        </div>
      </div>
    </>
  );
}
